import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { partnerPortalService } from '../services/api';
import {
  LayoutDashboard, Package, FileText, Building2, LogOut, Loader2, Plus, Send,
  Check, X, Upload, MessageSquare, ChevronLeft, ShieldCheck, CheckCircle2, XCircle, Clock,
} from 'lucide-react';

const money = (n) => (n == null || isNaN(Number(n)) ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d || '—'; } };
const STATUS_TONE = {
  SUBMITTED: 'bg-blue-100 text-blue-800', BLUELOCK_REVIEW: 'bg-indigo-100 text-indigo-800',
  QUOTING: 'bg-amber-100 text-amber-800', QUOTE_SHARED: 'bg-purple-100 text-purple-800',
  ACCEPTED: 'bg-green-100 text-green-800', DECLINED: 'bg-rose-100 text-rose-800',
  SCHEDULED: 'bg-cyan-100 text-cyan-800', IN_PROGRESS: 'bg-teal-100 text-teal-800',
  COMPLETED: 'bg-gray-200 text-gray-700',
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { key: 'requests', label: 'Shipments', Icon: Package },
  { key: 'quotes', label: 'Quotes', Icon: FileText },
  { key: 'company', label: 'Company', Icon: Building2 },
];

const PartnerPortalPage = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [me, setMe] = useState(null);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => { partnerPortalService.me().then(setMe).catch(() => setMe(null)); }, []);
  const perms = new Set(me?.permissions || []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-portal">
      <header className="bg-blue-900 text-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <p className="font-bold leading-tight truncate">BlueLock Partner Portal</p>
              <p className="text-[11px] text-blue-200 truncate" data-testid="partner-company-name">{me?.company_id ? (me?.name || user?.email) : ''}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="partner-logout" className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span></button>
        </div>
      </header>

      {/* Tab nav */}
      <nav className="bg-white border-b border-gray-200 sticky top-[52px] z-20">
        <div className="max-w-5xl mx-auto px-2 flex overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => { setTab(key); setDetailId(null); }} data-testid={`partner-tab-${key}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {!me ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
          <>
            {tab === 'dashboard' && <DashboardTab perms={perms} onOpen={(id) => { setTab('requests'); setDetailId(id); }} onNew={() => setTab('requests')} />}
            {tab === 'requests' && (detailId
              ? <RequestDetail id={detailId} perms={perms} onBack={() => setDetailId(null)} />
              : <RequestsTab perms={perms} onOpen={setDetailId} />)}
            {tab === 'quotes' && <QuotesTab perms={perms} />}
            {tab === 'company' && <CompanyTab perms={perms} />}
          </>
        )}
      </main>
    </div>
  );
};

const Card = ({ children, testid }) => <div data-testid={testid} className="bg-white border border-gray-200 rounded-xl p-4">{children}</div>;
const StatusPill = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_TONE[s] || 'bg-gray-100 text-gray-600'}`}>{(s || '').replace(/_/g, ' ')}</span>;

const DashboardTab = ({ onOpen, onNew }) => {
  const [d, setD] = useState(null);
  useEffect(() => { partnerPortalService.dashboard().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  return (
    <div className="space-y-4" data-testid="partner-dashboard-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card testid="partner-stat-total"><p className="text-[11px] text-gray-500">Total shipments</p><p className="text-2xl font-bold text-gray-900">{d.total_requests}</p></Card>
        <Card testid="partner-stat-quotes"><p className="text-[11px] text-gray-500">Shared quotes</p><p className="text-2xl font-bold text-gray-900">{d.shared_quotes}</p></Card>
        <Card testid="partner-stat-submitted"><p className="text-[11px] text-gray-500">Submitted</p><p className="text-2xl font-bold text-gray-900">{d.request_counts?.SUBMITTED || 0}</p></Card>
        <Card testid="partner-stat-progress"><p className="text-[11px] text-gray-500">In progress</p><p className="text-2xl font-bold text-gray-900">{(d.request_counts?.IN_PROGRESS || 0) + (d.request_counts?.SCHEDULED || 0)}</p></Card>
      </div>
      <Card testid="partner-recent">
        <div className="flex items-center justify-between mb-2"><h2 className="font-semibold text-gray-900">Recent shipments</h2>
          <button onClick={onNew} data-testid="partner-dashboard-new" className="text-sm text-blue-600 hover:underline">+ New request</button></div>
        {(d.recent_requests || []).length === 0 ? <p className="text-sm text-gray-500">No shipment requests yet.</p> : (
          <div className="divide-y">
            {d.recent_requests.map((r) => (
              <button key={r.id} onClick={() => onOpen(r.id)} className="w-full text-left py-2 flex items-center justify-between hover:bg-blue-50 rounded px-1" data-testid={`partner-recent-${r.id}`}>
                <span className="min-w-0"><span className="font-medium text-gray-900">{r.pickup_address || '—'} → {r.destination || '—'}</span><br /><span className="text-xs text-gray-500">{r.reference_number || r.id.slice(0, 8)} · {fmt(r.created_at)}</span></span>
                <StatusPill s={r.request_status} />
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const emptyShip = { pickup_business: '', pickup_address: '', pickup_contact: '', destination: '', destination_contact: '', pickup_date: '', delivery_date: '', freight_description: '', weight_lb: '', pieces: '', pallets: '', dimensions: '', special_handling: '', equipment_requirement: '', reference_number: '', po_number: '', bol_number: '', notes: '' };

const RequestsTab = ({ perms, onOpen }) => {
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => partnerPortalService.listRequests().then((d) => setRows(d.items || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="partner-requests-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Your shipment requests</h2>
        {perms.has('SUBMIT_JOBS') && <button onClick={() => setShowNew((s) => !s)} data-testid="partner-new-request-btn" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"><Plus className="w-4 h-4" /> New request</button>}
      </div>
      {showNew && <NewRequestForm onDone={() => { setShowNew(false); load(); }} onCancel={() => setShowNew(false)} />}
      {!rows ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? <Card><p className="text-sm text-gray-500">No requests yet.</p></Card> : (
          <div className="space-y-2">
            {rows.map((r) => (
              <button key={r.id} onClick={() => onOpen(r.id)} data-testid={`partner-request-row-${r.id}`} className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 flex items-center justify-between gap-2">
                <span className="min-w-0"><span className="font-medium text-gray-900 truncate block">{r.pickup_address || '—'} → {r.destination || '—'}</span><span className="text-xs text-gray-500">{r.reference_number || r.id.slice(0, 8)} · {r.weight_lb ? `${r.weight_lb} lb · ` : ''}{fmt(r.created_at)}</span></span>
                <StatusPill s={r.request_status} />
              </button>
            ))}
          </div>
        )}
    </div>
  );
};

const Field = ({ label, k, form, set, type = 'text', full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">{label}</label>
    <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`partner-field-${k}`} className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
  </div>
);

const NewRequestForm = ({ onDone, onCancel }) => {
  const [form, setForm] = useState(emptyShip);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const payload = { ...form, weight_lb: form.weight_lb === '' ? null : Number(form.weight_lb), pieces: form.pieces === '' ? null : Number(form.pieces), pallets: form.pallets === '' ? null : Number(form.pallets) };
      await partnerPortalService.submitRequest(payload); onDone();
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Submit failed.'); } finally { setSaving(false); }
  };
  return (
    <Card testid="partner-new-request-form">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="Pickup business" k="pickup_business" form={form} set={set} />
          <Field label="Pickup address" k="pickup_address" form={form} set={set} />
          <Field label="Pickup contact" k="pickup_contact" form={form} set={set} />
          <Field label="Destination" k="destination" form={form} set={set} />
          <Field label="Destination contact" k="destination_contact" form={form} set={set} />
          <Field label="Pickup date" k="pickup_date" form={form} set={set} type="date" />
          <Field label="Delivery date" k="delivery_date" form={form} set={set} type="date" />
          <Field label="Weight (lb)" k="weight_lb" form={form} set={set} type="number" />
          <Field label="Pieces" k="pieces" form={form} set={set} type="number" />
          <Field label="Pallets" k="pallets" form={form} set={set} type="number" />
          <Field label="Dimensions" k="dimensions" form={form} set={set} />
          <Field label="Equipment requirement" k="equipment_requirement" form={form} set={set} />
          <Field label="Reference #" k="reference_number" form={form} set={set} />
          <Field label="PO #" k="po_number" form={form} set={set} />
          <Field label="BOL #" k="bol_number" form={form} set={set} />
          <Field label="Freight description" k="freight_description" form={form} set={set} full />
          <Field label="Special handling" k="special_handling" form={form} set={set} full />
          <Field label="Notes" k="notes" form={form} set={set} full />
        </div>
        {err && <p role="alert" className="text-sm text-rose-600" data-testid="partner-new-request-error">{err}</p>}
        <div className="flex gap-2">
          <button type="submit" disabled={saving} data-testid="partner-submit-request" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit request</button>
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </Card>
  );
};

const RequestDetail = ({ id, perms, onBack }) => {
  const [r, setR] = useState(null);
  const [docs, setDocs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const load = useCallback(async () => {
    try {
      const [rr, dd, mm] = await Promise.all([
        partnerPortalService.getRequest(id), partnerPortalService.listDocuments(id), partnerPortalService.listMessages(id),
      ]);
      setR(rr); setDocs(dd.items || []); setMsgs(mm.items || []);
    } catch { setR(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', 'other');
    try { await partnerPortalService.uploadDocument(id, fd); await load(); } catch { /* ignore */ } finally { setUploading(false); e.target.value = ''; }
  };
  const send = async () => { if (!msg.trim()) return; await partnerPortalService.addMessage(id, msg.trim()); setMsg(''); load(); };
  const dl = async (docId) => { const res = await partnerPortalService.downloadDocument(id, docId); if (res?.download_url) window.open(res.download_url, '_blank'); };
  if (!r) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  return (
    <div className="space-y-4" data-testid="partner-request-detail">
      <button onClick={onBack} data-testid="partner-detail-back" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><ChevronLeft className="w-4 h-4" /> Back</button>
      <Card>
        <div className="flex items-center justify-between mb-2"><h2 className="font-semibold text-gray-900">{r.pickup_address || '—'} → {r.destination || '—'}</h2><StatusPill s={r.request_status} /></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
          {[['Reference', r.reference_number], ['Weight', r.weight_lb ? `${r.weight_lb} lb` : null], ['Pieces', r.pieces], ['Pallets', r.pallets], ['Pickup date', r.pickup_date], ['Delivery date', r.delivery_date], ['Equipment', r.equipment_requirement], ['Freight', r.freight_description], ['Special handling', r.special_handling]].map(([l, v]) => (
            <div key={l} className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">{l}</span><br /><b className="text-gray-800">{v || '—'}</b></div>
          ))}
        </div>
        {r.notes && <p className="text-xs text-gray-600 mt-2">Notes: {r.notes}</p>}
      </Card>

      <Card testid="partner-detail-docs">
        <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-gray-900 text-sm">Documents</h3>
          {perms.has('UPLOAD_DOCUMENTS') && <label className="inline-flex items-center gap-1 text-sm text-blue-600 cursor-pointer hover:underline" data-testid="partner-upload-label">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload<input type="file" className="hidden" onChange={upload} data-testid="partner-upload-input" /></label>}
        </div>
        {docs.length === 0 ? <p className="text-xs text-gray-500">No documents uploaded.</p> : (
          <div className="space-y-1">{docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-xs border border-gray-100 rounded px-2 py-1">
              <span className="truncate">{d.label || d.filename} <span className={`ml-1 px-1 rounded ${d.verified ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{d.verified ? 'VERIFIED' : 'RECEIVED'}</span></span>
              <button onClick={() => dl(d.id)} data-testid={`partner-doc-dl-${d.id}`} className="text-blue-600 hover:underline">Download</button>
            </div>
          ))}</div>
        )}
      </Card>

      <Card testid="partner-detail-messages">
        <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Messages</h3>
        <div className="space-y-2 mb-3 max-h-64 overflow-auto">
          {msgs.length === 0 ? <p className="text-xs text-gray-500">No messages yet.</p> : msgs.map((m) => (
            <div key={m.id} className={`text-xs rounded p-2 ${m.author_type === 'partner' ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}>
              <p className="text-gray-800">{m.body}</p><p className="text-[10px] text-gray-400 mt-0.5">{m.author_name} · {fmt(m.created_at)}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2"><input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message to BlueLock…" data-testid="partner-message-input" className="flex-1 py-1.5 px-2 text-sm border border-gray-300 rounded" /><button onClick={send} data-testid="partner-message-send" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Send</button></div>
      </Card>
    </div>
  );
};

const QuotesTab = ({ perms }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => partnerPortalService.listQuotes().then((d) => setRows(d.items || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, accept) => {
    setBusy(id);
    try { accept ? await partnerPortalService.acceptQuote(id) : await partnerPortalService.declineQuote(id); load(); }
    finally { setBusy(null); }
  };
  if (!rows) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  return (
    <div className="space-y-3" data-testid="partner-quotes-tab">
      <h2 className="font-semibold text-gray-900">Quotes shared with you</h2>
      {rows.length === 0 ? <Card><p className="text-sm text-gray-500">No quotes have been shared yet.</p></Card> : rows.map((q) => (
        <Card key={q.id} testid={`partner-quote-${q.id}`}>
          <div className="flex items-center justify-between mb-1"><span className="font-semibold text-gray-900">{q.quote_number || q.id.slice(0, 8)}</span><StatusPill s={(q.status || '').toUpperCase()} /></div>
          {q.shipment_summary && <p className="text-xs text-gray-600">{q.shipment_summary}</p>}
          <div className="mt-2 space-y-1">{(q.line_items || []).map((it, i) => (<div key={i} className="flex justify-between text-xs"><span>{it.description}</span><span>{money(it.amount ?? (it.quantity * it.unit_price))}</span></div>))}</div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100"><span className="text-sm text-gray-500">Total</span><span className="text-lg font-bold text-blue-900" data-testid={`partner-quote-total-${q.id}`}>{money(q.total)}</span></div>
          {q.customer_notes && <p className="text-xs text-gray-600 mt-1">{q.customer_notes}</p>}
          {q.valid_until && <p className="text-[11px] text-gray-400 mt-1">Valid until {q.valid_until}</p>}
          {perms.has('ACCEPT_QUOTES') && ['sent', 'viewed', 'quote_shared'].includes(q.status) && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => decide(q.id, true)} disabled={busy === q.id} data-testid={`partner-quote-accept-${q.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><Check className="w-4 h-4" /> Accept</button>
              <button onClick={() => decide(q.id, false)} disabled={busy === q.id} data-testid={`partner-quote-decline-${q.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"><X className="w-4 h-4" /> Decline</button>
            </div>
          )}
          {q.status === 'accepted' && <p className="text-xs text-green-700 mt-2 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Accepted — BlueLock will follow up. Acceptance does not bypass operational/compliance checks.</p>}
          {q.status === 'declined' && <p className="text-xs text-rose-700 mt-2 flex items-center gap-1"><XCircle className="w-4 h-4" /> Declined.</p>}
        </Card>
      ))}
    </div>
  );
};

const CompanyTab = ({ perms }) => {
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => partnerPortalService.getCompany().then((d) => { setC(d); setForm(d); }).catch(() => setC(null)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg(null);
    try { await partnerPortalService.updateCompany({ email: form.email, phone: form.phone, city: form.city, state: form.state, address_line1: form.address_line1, address_line2: form.address_line2, zip: form.zip }); setMsg('Saved.'); setEdit(false); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Save failed.'); } finally { setSaving(false); }
  };
  if (!c) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  return (
    <div className="space-y-3" data-testid="partner-company-tab">
      <div className="flex items-center justify-between"><h2 className="font-semibold text-gray-900">{c.name}</h2>
        {perms.has('MANAGE_COMPANY_CONTACTS') && !edit && <button onClick={() => setEdit(true)} data-testid="partner-company-edit" className="text-sm text-blue-600 hover:underline">Edit contact info</button>}</div>
      <Card>
        {!edit ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[['Legal name', c.legal_name], ['Type', c.company_type], ['Email', c.email], ['Phone', c.phone], ['Address', c.address_line1], ['City', c.city], ['State', c.state], ['ZIP', c.zip]].map(([l, v]) => (
              <div key={l}><span className="text-gray-500 text-xs">{l}</span><p className="text-gray-900">{v || '—'}</p></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Field label="Email" k="email" form={form} set={set} />
              <Field label="Phone" k="phone" form={form} set={set} />
              <Field label="Address line 1" k="address_line1" form={form} set={set} />
              <Field label="Address line 2" k="address_line2" form={form} set={set} />
              <Field label="City" k="city" form={form} set={set} />
              <Field label="State" k="state" form={form} set={set} />
              <Field label="ZIP" k="zip" form={form} set={set} />
            </div>
            <div className="flex gap-2"><button onClick={save} disabled={saving} data-testid="partner-company-save" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button><button onClick={() => { setEdit(false); setForm(c); }} className="px-4 py-2 text-sm border border-gray-300 rounded-md">Cancel</button></div>
          </div>
        )}
        {msg && <p className="text-xs text-gray-600 mt-2" data-testid="partner-company-msg">{msg}</p>}
      </Card>
    </div>
  );
};

export default PartnerPortalPage;
